import {
  Box,
  TextField,
  FormControl,
  InputAdornment,
  InputLabel,
  Input,
  IconButton,
  Button,
  Typography,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Divider,
  Paper,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  AccountCircleOutlined,
  Visibility,
  VisibilityOff,
  LockOutlined,
  Group,
  FitScreen,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { set } from "lodash";
import { Tabs } from "../../../components/tabs";
import { Grid } from "../../../components/table";
import { CONFIG } from "../../../../config";
import axios from "axios";
import { ToolBar } from "../../../components/toolbar";
import { Modal } from "../../../components/modal";
import { FormA } from "../../../components/form";
import moment from "moment";
import { getFormState } from "../../../../helpers/form";
import dayjs from "dayjs";
import { SelectA, FileUploadA, DatePickerA } from "../../../components/form";
import DateRangePicker from "./components/date-range-picker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AllocationUpload } from "../upload";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from "chart.js";
import { IosShareOutlined } from "@mui/icons-material";
import { Doughnut, Bar, Line, Chart } from "react-chartjs-2";
import SortIndicator from "./components/sortable-column-head";
import AgentSummaryTable from "./components/agent-summary-table";
import ProductivityCountTable from "./components/productivity-count-table";
import FullScreenLoader from "../../caller/components/fullscreen-loader";
import { useAnalyticsSyncDate } from "./context/analytics-sync-date.context";
ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController
);

const options = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 12,
      right: 12,
      bottom: 8,
      left: 12,
    },
  },
  scales: {
    x: {
      ticks: {
        padding: 6,
      },
    },
    y: {
      ticks: {
        padding: 6,
      },
    },
  },
  elements: {
    point: {
      radius: 5,
      hoverRadius: 6,
    },
  },
  plugins: {
    datalabels: {
      align: "top",
      anchor: "end",
      offset: 6,
      clamp: true,
      color: "#333",
      font: {
        size: 10,
        weight: "bold",
      },
    },
    legend: {
      position: "bottom",
      labels: {
        padding: 12,
      },
    },
  },
};

const API_HOST = CONFIG.modules["accounts"].host;
const API_HOST_CRM = CONFIG.modules["crm"].host;

const callSummaryMap = {
  "Total allocated calls": "allocated",
  "Total attempted calls": "attempted",
  // "Total productive calls": "productive", // 13th February 2026 - Changed to connected as per client request
  "Total connected calls": "productive",
  "Total inbound calls": "inbound",
  "Daily average call attempts": "attemptAverage",
  // "Daily average productive calls": "productiveAverage", // 13th February 2026 - Changed to connected as per client request
  "Daily average connected calls": "productiveAverage",
};

const agentSummaryMap = {
  Allocations: "allocated",
  Productive: "productive",
  UnProductive: "unProductive",
  Attempted: "attempted",
  Pending: "pending",
  ChildAbove2Years: "childAbove2Years",
  Declined: "declined",
  NotInThePortal: "notInThePortal",
  "Productivity %": "productivityP",
  "Attempted %": "attemptedP",
};

const sortOperationMap = {
  "neutral": null,
  "asc": "asc",
  "desc": "desc",
}

export const Overall = (props) => {
  const {
    roles,
    user,
    users,
    Designations,
    Departments,
    Languages,
    Districts,
    Campaigns,
  } = useSelector((state) => state.user || {});
  const {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    enableDateSync,
    setEnableDateSync,
  } = useAnalyticsSyncDate();
  const [filter, setFilter] = useState({
    campaign: "ecd",
    telecaller: "All",
  });
  const [callsDayWise, setCallsDayWise] = useState(null);
  const [callSummary, setCallSummary] = useState(null);
  const [callSummaryRows, setCallSummaryRows] = useState([]);
  const [agentSummary, setAgentSummary] = useState(null);
  const [agentSummaryRows, setAgentSummaryRows] = useState([]);
  const [datewiseSummary, setDatewiseSummary] = useState(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // State for storing a map of the columns and what is is being sorted as
  const [agentSummarySortMap, setAgentSummarySortMap] = useState({
    "Allocations": sortOperationMap.neutral,
    "Productive": sortOperationMap.neutral,
    "UnProductive": sortOperationMap.neutral,
    "Attempted": sortOperationMap.neutral,
    "Pending": sortOperationMap.neutral,
    "ChildAbove2Years": sortOperationMap.neutral,
    "Declined": sortOperationMap.neutral,
    "NotInThePortal": sortOperationMap.neutral,
    "Productivity %": sortOperationMap.neutral,
    "Attempted %": sortOperationMap.neutral,
  });


  /**
   * Updates the map of the columns and what is is being sorted as
   * @param {string} column - The column to sort.
   * @param {'asc' | 'desc' | null} direction - The direction to sort.
   * unused for now, sorting logic moved to the table component itself
   */
  const handleSortSelection = (column, direction) => {
    setAgentSummarySortMap({
      ...agentSummarySortMap,
      [column]: direction,
    });
  };


  useEffect(() => {
    let isActive = true;
    const fetchFilterData = async () => {
      setIsFilterLoading(true);
      try {
        await Promise.all([
          getCallSummary(),
          getAgentSummary(),
          getDateWiseSummary(),
        ]);
      } finally {
        if (isActive) {
          setIsFilterLoading(false);
        }
      }
    };

    fetchFilterData();
    return () => {
      isActive = false;
    };
  }, [filter, fromDate, toDate]);

  const buildCommonQuery = () => {
    const params = new URLSearchParams();
    if (fromDate) params.set("start", dayjs(fromDate).format("YYYY-MM-DD"));
    if (toDate) params.set("end", dayjs(toDate).format("YYYY-MM-DD"));
    if (filter.telecaller) params.set("caller", filter.telecaller);
    return params.toString();
  };

  const getDateWiseSummary = () => {
    return axios
      .get(
        API_HOST_CRM +
        `/get-datewise-summary-all?${buildCommonQuery()}`
        // commented out the old route
        // `/get-datewise-summary-all?${buildCommonQuery()}`
      )
      .then((resp) => {
        if (resp.data && resp.data.success) {
          setDatewiseSummary(resp.data.data);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getCallSummary = () => {
    return axios
      .get(
        API_HOST_CRM +
        `/get-call-summary-all?${buildCommonQuery()}`
        // commented out the old route
        // `/get-call-summary?${buildCommonQuery()}`
      )
      .then((resp) => {
        if (resp.data && resp.data.success) {
          setCallSummary(resp.data.data);
          const callSummaryRows = Object.keys(resp.data.data).map(key => ({
            key,
            value: resp.data.data[key]
          }));
          setCallSummaryRows(callSummaryRows);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getAgentSummary = () => {
    return axios
      .get(
        API_HOST_CRM +
        `/get-agent-summary-all?${buildCommonQuery()}`
        // commented out the old route
        // `/get-agent-summary?${buildCommonQuery()}`
      )
      .then((resp) => {
        if (resp.data && resp.data.success) {

          // Create a map of the users using its id and only keep those where the active flag is true
          // This will help us only keeping the active users in the agent summary and also get the name of the user for the agent summary table
          const activeUsersMap = {};
          (users || []).forEach(user => {
            if (user.active) {
              activeUsersMap[user._id] = user;
            }
          });

          setAgentSummary(resp.data.data);
          /**
           * The shape of the data.data meaning agentSummary is as follows:
           * {
           * "userId": {
           *  "allocated": 10,
           *  "productive": 10,
           *  "unProductive": 10,
           *  "attempted": 10,
           *  "pending": 10,
           *  "childAbove2Years": 10,
           *  "declined": 10,
           *  "notInThePortal": 10,
           *  "productivityP": 10,
           *  "attemptedP": 10,
           * "telecaller": "telecallerName"
           * }
           * 
           * So we will have to go create an array of objects and also include the userId in the object.
           */

          // Ok we will need rows that are not there as well so we need to loop over the users and if data exists add them in the row otherwise all the properties will be 0
          const agentSummaryRows = [];
          (users || []).filter((e) =>
            e.roles.includes(process.env.REACT_APP_TELECALLER)
          ).forEach(user => {
            // This entire state update for the agentSummaryRows should
            // happen only if the user._id is in the usersMap, this is to avoid having the rows for the users who are not active
            if (activeUsersMap[user._id]) {
              if (resp.data.data[user._id]) {
                agentSummaryRows.push({
                  userId: user._id,
                  ...resp.data.data[user._id]
                });
              } else {
                // This else block is used to keep default 0 values if there is no data for a particular user
                // in the response from the backend, we will use the agentSummaryMap to get all the keys and set them to 0
                agentSummaryRows.push({
                  userId: user._id,
                  telecaller: user.name,
                  ...Object.fromEntries(Object.keys(agentSummaryMap).map(key => [key, 0]))
                });
              }
            }
          });
          setAgentSummaryRows(agentSummaryRows);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getCallsDayWiseData = () => {
    axios
      .get(API_HOST_CRM + `/get-dashboard-data?type=callsDayWise`)
      .then((resp) => {
        if (resp.data && resp.data.success) {
          setCallsDayWise(resp.data.data);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleExportInExcel = () => {
    axios
      .get(
        API_HOST_CRM +
        `/get-agent-summary-all?exportData=yes&${buildCommonQuery()}`,
        {
          responseType: "arraybuffer",
        }
      )

      .then((resp) => {
        if (resp) {
          console.log("The response from the new controller", resp);
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Analytics_Report.xlsx`);
          document.body.appendChild(link);
          link.click();
          console.log(resp);
          link.remove();
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    getCallsDayWiseData();
  }, []);

  const handleFromDateChange = (newDate) => {
    setFromDate(newDate ? dayjs(newDate) : null);
  };

  const handleToDateChange = (newDate) => {
    setToDate(newDate ? dayjs(newDate) : null);
  };

  const handleClearFilter = () => {
    setFromDate(null);
    setToDate(null);
    setFilter({
      campaign: "ecd",
      telecaller: null,
      cfrom: null,
      cto: null,
      firstReceived: null,
      motherName: "",
      motherID: "",
      contact: "",
      status: null,
      motherStatus: null,
      district: null,
    });
  };

  return (
    <>
      <FullScreenLoader open={isFilterLoading} />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", width: "100%", px: 1, pt: 1 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <DateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              fromLabel="Assign From"
              toLabel="Assign To"
              onFromChange={handleFromDateChange}
              onToChange={handleToDateChange}
            />
            <FormControlLabel
              sx={{ ml: { md: 2 }, whiteSpace: "nowrap" }}
              control={
                <Switch
                  checked={Boolean(enableDateSync)}
                  onChange={(event) => setEnableDateSync(event.target.checked)}
                  color="primary"
                />
              }
              label="Sync date across analytics"
            />
          </Stack>
        </Box>
      </LocalizationProvider>

      <ToolBar
        name={""}
        height={"5em"}
        buttons={[
          {
            name: "Export",
            icon: () => <IosShareOutlined sx={{ fontSize: "1.2em" }} />,
            onClick: () => {
              handleExportInExcel();
            },
          },
        ]}
        onClearFilter={handleClearFilter}
        filters={[
          {
            field: {
              name: "campaign",
              type: "select",
              label: "Campaign",
              option: { key: "Campaigns", value: "_id", label: "name" },
            },
            onChange: (name, value) => {
              setFilter((prev) => ({
                ...prev,
                [name]: value,
              }));
            },
            data: {
              Campaigns: [{ _id: "ecd", name: "ECD" }],
            },
            state: filter,
          },
          {
            field: {
              name: "telecaller",
              type: "select",
              label: "Telecaller",
              option: { key: "Telecallers", value: "_id", label: "name" },
            },
            onChange: (name, value) => {
              setFilter((prev) => ({
                ...prev,
                [name]: value,
              }));
            },
            data: {
              Telecallers: [
                {
                  _id: "All",
                  name: "All",
                },
                ...(users || []).filter((e) =>
                  e.roles.includes(process.env.REACT_APP_TELECALLER)
                ),
              ],
            },
            state: filter,
          },
          // NOTE: Temporarily disabled date filters here so the Overall view sends
          // start/end consistently as YYYY-MM-DD (aligned with Agent Analytics view).
          // {
          //   field: {
          //     name: "from",
          //     type: "date",
          //     label: "Assign From",
          //   },
          //   onChange: (name, value) => {
          //     filter[name] = value;
          //     setFilter({ ...filter });
          //   },
          //   state: filter,
          // },
          // {
          //   field: {
          //     name: "to",
          //     type: "date",
          //     label: "Assign To",
          //   },
          //   onChange: (name, value) => {
          //     filter[name] = value;
          //     setFilter({ ...filter });
          //   },
          //   state: filter,
          // },
        ]}
      />
      <Box sx={{ display: "flex", width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", width: "75%" }}>
          <Box sx={{ display: "flex", width: "100%" }}>
            <Box sx={{ display: "flex", width: "60%", p: 1 }}>
              <Paper
                elevation={3}
                sx={{
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                  pl: 3,
                  pr: 3,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {/* 13th February 2026 - Changed to Connected as per client request */}
                    Total call vs Connected calls
                  </Typography>
                  <Box sx={{ height: 260, position: "relative", pt: 1 }}>
                    {datewiseSummary && (
                      <Chart
                        options={options}
                        type="bar"
                        data={{
                          labels: Object.keys(datewiseSummary).map((e) =>
                            dayjs(e).format("DD-MMM")
                          ),
                          datasets: [
                            {
                              label: "UnProductive",
                              data: Object.values(datewiseSummary).map(
                                (e) => e.unProductive
                              ),
                              borderColor: "#E9202A",
                              // backgroundColor: '#E9202A',
                              borderWidth: 2,
                              type: "line",
                              fill: false,
                            },
                            {
                              // 13th February 2026 - Changed to Connected as per client request
                              label: "Connected",
                              data: Object.values(datewiseSummary).map(
                                (e) => e.productive
                              ),
                              borderColor: "#169055",
                              // backgroundColor: '#169055',
                              borderWidth: 2,
                              type: "line",
                              fill: false,
                            },
                            {
                              label: "Total Allocations",
                              data: Object.values(datewiseSummary).map(
                                (e) => e.allocated
                              ),
                              borderColor: "#60C7EE",
                              backgroundColor: "#60C7EE",
                              type: "bar",
                            },
                          ],
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ display: "flex", width: "40%", p: 1 }}>
              <Paper
                elevation={3}
                sx={{
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                  pl: 3,
                  pr: 3,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Call summary
                  </Typography>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderBottom: ".5px solid grey",
                        p: 1,
                        pl: 0,
                        pr: 0,
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: "left",
                          display: "flex",
                          color: "rgba(0, 0, 0, 0.4)",
                        }}
                      >
                        <Typography sx={{ fontSize: ".9em" }}>
                          Particular
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          textAlign: "right",
                          display: "flex",
                          color: "rgba(0, 0, 0, 0.4)",
                        }}
                      >
                        <Typography sx={{ fontSize: ".9em" }}>Count</Typography>
                      </Box>
                    </Box>
                    {callSummary &&
                      [
                        "Total allocated calls",
                        "Total attempted calls",
                        // "Total productive calls", // 13th February 2026 - Changed to connected as per client request
                        "Total connected calls",
                        "Total inbound calls",
                        "Daily average call attempts",
                        // "Daily average productive calls", // 13th February 2026 - Changed to connected as per client request
                        "Daily average connected calls",
                      ].map((a, i) => {
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderBottom: ".5px solid grey",
                              p: 1,
                              pl: 0,
                              pr: 0,
                            }}
                            key={i}
                          >
                            <Box
                              sx={{
                                textAlign: "left",
                                display: "flex",
                                color: "rgba(0, 0, 0, 0.7)",
                              }}
                            >
                              <Typography sx={{ fontSize: ".9em" }}>
                                {a}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                textAlign: "right",
                                display: "flex",
                                color: "rgba(0, 0, 0, 0.7)",
                              }}
                            >
                              <Typography sx={{ fontSize: ".9em" }}>
                                {callSummary[callSummaryMap[a]] || 0}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>

          <Box sx={{ display: "flex", width: "100%" }}>
            <Box sx={{ display: "flex", width: "100%", p: 1 }}>
              <Paper
                elevation={3}
                sx={{
                  width: "100%",
                  overflowX: "scroll",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                  pl: 3,
                  pr: 3,
                }}
              >
                {/* There is old view of the agent summary here */}


                {/* End old view of the agent summary here */}
                <AgentSummaryTable
                  rows={agentSummaryRows}
                  maxHeight="30vh"
                />
              </Paper>
            </Box>
          </Box>
        </Box >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "25%",
            flex: 1,
            // border: "1px solid purple",
            // make this position relative
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100%",
              p: 1,
              flexGrow: 1,
              //Make this position absolute
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                p: 2,
                pl: 3,
                pr: 3,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "95%",
                  //   overflowY: "scroll",
                  //   justifyContent: "center",
                  //   alignItems: "center",
                  //   height: "100%",
                }}
              >
                <ProductivityCountTable
                  rows={agentSummaryRows}
                // maxHeight="30vh"
                />

                {/* <Typography
                  sx={{
                    fontWeight: "bold",
                    color: "#F28627",
                    fontSize: ".8em",
                  }}
                >
                  Productivity count by telecallers
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    height: "100%",
                    // height: "50vh",
                    overflowY: "scroll",
                  }}
                >
                  {(users || [])
                    .filter((e) =>
                      e.roles.includes(process.env.REACT_APP_TELECALLER)
                    )
                    .map((a, i) => {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            p: 1,
                            pr: 0,
                            pl: 0,
                          }}
                          key={i}
                        >
                          <Box>
                            <Typography sx={{ fontSize: ".8em" }}>
                              {a.name}
                            </Typography>
                          </Box>
                          <Box sx={{ pr: 2 }}>
                            {agentSummary && (
                              <Typography sx={{ fontSize: ".8em" }}>
                                {agentSummary[a._id]?.productive || "0"}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                </Box> */}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box >
    </>
  );
};

/**
 * Previous view of the agent summary
 *    <Box
                  sx={{
                    minWidth: "fit-content",
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <Box sx={{
                      // position: "sticky",
                      left: 0,
                      zIndex: 1000,
                      backgroundColor: "white",
                      display: "flex", minWidth: "10rem", maxWidth: "10rem", textWrap: "wrap",
                      borderRight: "1px solid grey",
                    }}>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          color: "#F28627",
                          fontSize: ".8em",
                        }}
                      >
                        Agents
                      </Typography>
                    </Box>
    
<Box
  sx={{
    display: "flex",
    // width: "85%",
    fontWeight: "bold",
    justifyContent: "space-between",
  }}
>
  {[
    "Allocations",
    "Productive",
    "UnProductive",
    "Attempted",
    "Pending",
    "ChildAbove2Years",
    "Declined",
    "NotInThePortal",
    "Productivity %",
    "Attempted %",
  ].map((a, i) => {
    return (
      <Box
        key={i}
        sx={{ textAlign: "center" }}
      >
        <SortIndicator
          direction={agentSummarySortMap[a]}
          label={a}
          key={a}
          onClick={handleSortSelection}
        />

      </Box>
    );
  })}
</Box>

                  </Box >

  < Box sx = {{
  mt: 1.5, height: "30vh",
    overflowY: "scroll",
      // display: "flex",
      flexDirection: "column",
                    // border: "1px solid red"
                  }}>
  {(users || [])
    .filter((e) =>
      e.roles.includes(process.env.REACT_APP_TELECALLER)
    )
    .map((a, i) => {
      return (
        <Box
          sx={{
            display: "flex", p: 1, pl: 0, pr: 0,
          }}
          key={i}
        >
          <Box sx={{
            display: "flex",
            minWidth: "10rem",
            maxWidth: "10rem",
            textWrap: "wrap",
            // position: "sticky",
            left: 0,
            zIndex: 1000,
            backgroundColor: "white",
            borderRight: "1px solid grey",
          }}>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.7)",
                fontSize: ".8em",
              }}
            >
              {a.name}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              fontWeight: "bold",
              justifyContent: "space-between",
            }}
          >
            {[
              "Allocations",
              "Productive",
              "UnProductive",
              "Attempted",
              "Pending",
              "ChildAbove2Years",
              "Declined",
              "NotInThePortal",
              "Productivity %",
              "Attempted %",
            ].map((e, i) => {
              return (
                <Box
                  key={i}
                  sx={{
                    width: "100%",
                    textAlign: "center"
                  }}
                >
                  {agentSummary && (
                    <Typography
                      sx={{
                        color: "rgba(0, 0, 0, 0.7)",
                        fontSize: ".8em",
                      }}
                    >
                      {agentSummary[a._id]?.[
                        agentSummaryMap[e]
                      ] || "0"}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    })}
                  </Box >
                </Box >

 */


/**
 * Previous view of the productivity count by telecallers
 * 
 *  <Typography
  sx={{
    fontWeight: "bold",
    color: "#F28627",
    fontSize: ".8em",
  }}
>
  Productivity count by telecallers
</Typography>
<Box
  sx={{
    mt: 1.5,
    height: "100%",
    // height: "50vh",
    overflowY: "scroll",
  }}
>
  {(users || [])
    .filter((e) =>
      e.roles.includes(process.env.REACT_APP_TELECALLER)
    )
    .map((a, i) => {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 1,
            pr: 0,
            pl: 0,
          }}
          key={i}
        >
          <Box>
            <Typography sx={{ fontSize: ".8em" }}>
              {a.name}
            </Typography>
          </Box>
          <Box sx={{ pr: 2 }}>
            {agentSummary && (
              <Typography sx={{ fontSize: ".8em" }}>
                {agentSummary[a._id]?.productive || "0"}
              </Typography>
            )}
          </Box>
        </Box>
      );
    })}
</Box>
 */